// @flow
import type { FSA } from 'types/redux';
import type { Booking } from 'types/bookings';
import type {
  Cohort,
  Cohorts,
  Driver,
  Drivers,
  Passenger,
  Passengers,
  Merchants,
} from 'types/entities';
import type {
  AppState,
} from 'reducers/app';
import {
  BOOKING_STATE_NEW,
  BOOKING_STATE_JOB_CANCELLED,
} from 'types/bookings';

import _ from 'lodash';
import {
  COHORT_CREATE,
  COHORT_GET,
  COHORT_DELETE,
  COHORT_PASSENGER_ASSOCIATE,
  COHORT_PASSENGER_UPDATE,
  COHORT_DRIVER_ASSOCIATE,
  COHORT_DRIVER_UPDATE,
  UNSET_COHORT,
} from 'actions/cohorts';
import {
  PASSENGER_LOGIN,
  PASSENGER_LOGOUT,
  PASSENGER_DELETE,
  PASSENGER_BOOKING_GET,
  PASSENGER_BOOKING_CANCEL,
  PASSENGER_BOOKING_NEW,
  PASSENGER_BOOKING_STATE_CHANGE,
  PASSENGER_BOOKING_DELETE,
  PASSENGER_CHANGE_DESTINATION,
} from 'actions/passengers';
import {
  DRIVER_LOGIN,
  DRIVER_LOGOUT,
  DRIVER_DELETE,
  DRIVER_SET_AVAILABILITY,
  DRIVER_SET_AUTO_ACCEPT,
  DRIVER_SET_LOCATION,
  DRIVER_BOOKING_NEW,
  DRIVER_BOOKING_STATE_CHANGE,
  DRIVER_BOOKING_DELETE,
  DRIVER_BATCH_SET_LOCATION,
} from 'actions/drivers';

import * as RequestResultCases from 'middlewares/api-middleware';

export const defaultPassengersState: Passengers = {};
export const defaultDriversState: Drivers = {};
export const defaultMerchantsState: Merchants = {};
export const defaultCohortState: Cohort = {
  cohortID: '',
  passengers: defaultPassengersState,
  drivers: defaultDriversState,
  merchants: defaultMerchantsState,
};
export const defaultCohortsState: Cohorts = {};

export const autoAcceptValueOff = 0;
export const autoAcceptValueNA = -1;
export const autoAcceptValueOn = 1;

export function passengerBooking(state: Booking, action: FSA): Booking {
  switch (action.type) {
    case PASSENGER_BOOKING_NEW:
      return {
        ...state,
        state: BOOKING_STATE_NEW,
        ...action.payload.booking,
      };
    case PASSENGER_BOOKING_STATE_CHANGE:
      return {
        ...state,
        state: action.payload.state,
      };
    case PASSENGER_BOOKING_CANCEL + RequestResultCases.SUCCESS:
      return {
        ...state,
        state: BOOKING_STATE_JOB_CANCELLED,
      };
    case PASSENGER_BOOKING_GET + RequestResultCases.SUCCESS:
      return {
        ...state,
        ...action.payload,
      };
    case PASSENGER_CHANGE_DESTINATION + RequestResultCases.SUCCESS:
      return {
        ...state,
        itinerary: _.concat([_.first(state.itinerary)], action.meta ? {
          ...action.meta.dropoffPoint,
        } : _.last(state.itinerary)),
      };
    default:
      return state;
  }
}

export function passengerBookings(state: Array<Booking> = [], action: FSA): Array<Booking> {
  switch (action.type) {
    case PASSENGER_BOOKING_NEW: {
      const index = _.findIndex(state, (booking: Booking) => booking.bookingID === action.payload.bookingID);
      if (index !== -1) {
        return state;
      }
      return [passengerBooking(action.payload.booking, action), ...state];
    }
    case PASSENGER_BOOKING_STATE_CHANGE:
      return state.map((booking: Booking) => {
        if (booking.bookingID === action.payload.bookingID) {
          return passengerBooking(booking, action);
        }
        return booking;
      });
    case PASSENGER_BOOKING_GET + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_CANCEL + RequestResultCases.SUCCESS:
    case PASSENGER_CHANGE_DESTINATION + RequestResultCases.SUCCESS:
      return state.map((booking: Booking) => {
        if (action.meta && booking.bookingID === action.meta.bookingID) {
          return passengerBooking(booking, action);
        }
        return booking;
      });
    case PASSENGER_BOOKING_DELETE:
      return state.filter((booking: Booking) => booking.bookingID !== action.payload.bookingID);
    default:
      return state;
  }
}

export function passenger(state: Passenger, action: FSA): Passenger {
  switch (action.type) {
    case COHORT_PASSENGER_ASSOCIATE: {
      const newState: Passenger = action.payload.passenger;
      // If passenger is not logged in, do not overwrite the behaviour received
      // from the API because it is necessarily empty.
      if (!action.payload.passenger.loggedIn && state) {
        newState.behaviour = state.behaviour;
      }
      return newState;
    }
    case COHORT_GET + RequestResultCases.SUCCESS: {
      if (!action.meta) {
        return state;
      }
      const newState: Passenger = action.payload.passengers[action.meta.passengerID];
      if (!newState.loggedIn && state) {
        newState.behaviour = state.behaviour;
      }
      return newState;
    }
    case COHORT_PASSENGER_UPDATE:
      return {
        ...state,
        ...action.payload.passenger,
      };
    case PASSENGER_LOGIN:
      return {
        ...state,
        loggedIn: true,
        behaviour: action.payload.behaviour,
      };
    case PASSENGER_LOGIN + RequestResultCases.SUCCESS:
      return {
        ...state,
        loggedIn: true,
      };
    case PASSENGER_LOGOUT:
    case PASSENGER_LOGOUT + RequestResultCases.SUCCESS:
      return {
        ...state,
        loggedIn: false,
      };
    case PASSENGER_BOOKING_NEW:
    case PASSENGER_BOOKING_GET + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_STATE_CHANGE:
    case PASSENGER_BOOKING_CANCEL + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_DELETE:
    case PASSENGER_CHANGE_DESTINATION + RequestResultCases.SUCCESS:
      return {
        ...state,
        bookings: passengerBookings(state.bookings, action),
      };
    default:
      return state;
  }
}

export function passengers(state: Passengers = defaultPassengersState, action: FSA): Passengers {
  switch (action.type) {
    case COHORT_GET + RequestResultCases.SUCCESS:
      return _.mapValues(action.payload.passengers, (pax: Passenger, passengerID: string) => {
        if (state[passengerID]) {
          return passenger(state[passengerID], {
            ...action,
            meta: {
              ...action.meta,
              passengerID,
            },
          });
        }
        return pax;
      });
    case COHORT_PASSENGER_ASSOCIATE:
      return {
        ...state,
        [action.payload.passengerID]: passenger(state[action.payload.passengerID], action),
      };
    case COHORT_PASSENGER_ASSOCIATE + RequestResultCases.SUCCESS:
      {
        if (!action.meta) {
          return state;
        }
        return {
          ...state,
          [action.payload.passengerID]: {
            ...action.meta.passenger,
            passengerID: action.payload.passengerID,
            loggedIn: false,
            bookings: [],
          },
        };
      }
    case PASSENGER_LOGIN + RequestResultCases.SUCCESS:
    case PASSENGER_LOGOUT + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_GET + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_CANCEL + RequestResultCases.SUCCESS:
    case PASSENGER_CHANGE_DESTINATION + RequestResultCases.SUCCESS:
      if (!action.meta) {
        return state;
      }
      return {
        ...state,
        [action.meta.passengerID]: passenger(state[action.meta.passengerID], action),
      };
    case PASSENGER_DELETE + RequestResultCases.SUCCESS:
      if (!action.meta) {
        return state;
      }
      return _.omit(state, action.meta.passengerID);
    case COHORT_PASSENGER_UPDATE:
    case PASSENGER_LOGIN:
    case PASSENGER_LOGOUT:
    case PASSENGER_BOOKING_NEW:
    case PASSENGER_BOOKING_STATE_CHANGE:
    case PASSENGER_BOOKING_DELETE: {
      if (!state[action.payload.passengerID]) {
        // Passenger does not exist.
        return state;
      }
      return {
        ...state,
        [action.payload.passengerID]: passenger(state[action.payload.passengerID], action),
      };
    }
    case PASSENGER_DELETE:
      return _.omit(state, action.payload.passengerID);
    default:
      return state;
  }
}

export function driverBooking(state: Booking, action: FSA): Booking {
  switch (action.type) {
    case DRIVER_BOOKING_NEW:
      return {
        ...state,
        ...action.payload.booking,
      };
    case DRIVER_BOOKING_STATE_CHANGE:
      return {
        ...state,
        state: action.payload.state,
      };
    default:
      return state;
  }
}

export function driverBookings(state: Array<Booking> = [], action: FSA): Array<Booking> {
  switch (action.type) {
    case DRIVER_BOOKING_NEW: {
      const index = _.findIndex(state, (booking: Booking) => booking.bookingID === action.payload.bookingID);
      if (index !== -1) {
        return state;
      }
      return [driverBooking(action.payload.booking, action), ...state];
    }
    case DRIVER_BOOKING_STATE_CHANGE:
      return state.map((booking: Booking) => {
        if (booking.bookingID === action.payload.bookingID) {
          return driverBooking(booking, action);
        }
        return booking;
      });
    case DRIVER_BOOKING_DELETE:
      return state.filter((booking: Booking) => booking.bookingID !== action.payload.bookingID);
    default:
      return state;
  }
}

export function driver(state: Driver, action: FSA, app: AppState): Driver {
  let autoAcceptValue;
  switch (action.type) {
    case COHORT_DRIVER_ASSOCIATE: {
      const newState: Driver = action.payload.driver;
      // If driver is not logged in, do not overwrite the behaviour received
      // from the API because it is necessarily empty.
      if (!action.payload.driver.loggedIn && state) {
        newState.behaviour = state.behaviour;
        newState.latitude = state.latitude;
        newState.longitude = state.longitude;
      }
      return newState;
    }
    case COHORT_GET + RequestResultCases.SUCCESS: {
      if (!action.meta) {
        return state;
      }
      const newState: Driver = action.payload.drivers[action.meta.driverID];
      if (!newState.loggedIn && state) {
        newState.behaviour = state.behaviour;
        newState.latitude = state.latitude;
        newState.longitude = state.longitude;
        newState.autoAccept = state.autoAccept;
      }
      return newState;
    }
    case COHORT_DRIVER_UPDATE:
      return {
        ...state,
        ...action.payload.driver,
      };
    case DRIVER_LOGIN:
      return {
        ...state,
        loggedIn: true,
        available: true,
        behaviour: action.payload.behaviour,
      };
    case DRIVER_LOGIN + RequestResultCases.SUCCESS:
      return {
        ...state,
        loggedIn: true,
        available: true,
      };
    case DRIVER_LOGOUT:
    case DRIVER_LOGOUT + RequestResultCases.SUCCESS:
      return {
        ...state,
        loggedIn: false,
      };
    case DRIVER_SET_AVAILABILITY:
      return {
        ...state,
        available: action.payload.availability,
      };
    case DRIVER_SET_AVAILABILITY + RequestResultCases.SUCCESS:
      return {
        ...state,
        available: action.meta && action.meta.availability,
      };
    case DRIVER_SET_AUTO_ACCEPT:
      if (action.payload.isTurnedOn) {
        autoAcceptValue = autoAcceptValueOn;
      } else {
        autoAcceptValue = autoAcceptValueOff;
      }
      return {
        ...state,
        autoAccept: autoAcceptValue,
      };
    case DRIVER_SET_AUTO_ACCEPT + RequestResultCases.SUCCESS:
      if (action.meta && action.meta.isTurnedOn) {
        autoAcceptValue = autoAcceptValueOn;
      } else {
        autoAcceptValue = autoAcceptValueOff;
      }
      return {
        ...state,
        autoAccept: autoAcceptValue,
      };
    case DRIVER_SET_LOCATION:
      if (app.driverBeingDragged === action.payload.driverID && !action.payload.selfGenerated) {
        return state;
      }
      return {
        ...state,
        longitude: action.payload.location[0],
        latitude: action.payload.location[1],
      };
    case DRIVER_BOOKING_NEW:
    case DRIVER_BOOKING_STATE_CHANGE:
    case DRIVER_BOOKING_DELETE:
      return {
        ...state,
        bookings: driverBookings(state.bookings, action),
      };
    default:
      return state;
  }
}

function drivers(state: Drivers = defaultDriversState, action: FSA, app: AppState): Drivers {
  switch (action.type) {
    case COHORT_GET + RequestResultCases.SUCCESS:
      return _.mapValues(action.payload.drivers, (dax: Driver, driverID: string) => {
        if (state[driverID]) {
          return driver(state[driverID], {
            ...action,
            meta: {
              ...action.meta,
              driverID,
            },
          }, app);
        }
        const resDax = dax;
        resDax.autoAccept = -1;
        return resDax;
      });
    case COHORT_DRIVER_ASSOCIATE:
      return {
        ...state,
        [action.payload.driverID]: driver(state[action.payload.driverID], action, app),
      };
    case COHORT_DRIVER_ASSOCIATE + RequestResultCases.SUCCESS:
      {
        if (!action.meta) {
          return state;
        }
        return {
          ...state,
          [action.payload.driverID]: {
            ...action.meta.driver,
            driverID: action.payload.driverID,
            loggedIn: false,
            available: false,
            autoAccept: -1,
            bookings: [],
          },
        };
      }
    case DRIVER_LOGIN + RequestResultCases.SUCCESS:
    case DRIVER_LOGOUT + RequestResultCases.SUCCESS:
    case DRIVER_SET_AUTO_ACCEPT + RequestResultCases.SUCCESS:
    case DRIVER_SET_AVAILABILITY + RequestResultCases.SUCCESS:
      if (!action.meta) {
        return state;
      }
      return {
        ...state,
        [action.meta.driverID]: driver(state[action.meta.driverID], action, app),
      };
    case DRIVER_DELETE + RequestResultCases.SUCCESS:
      if (!action.meta) {
        return state;
      }
      return _.omit(state, action.meta.driverID);
    case COHORT_DRIVER_UPDATE:
    case DRIVER_LOGIN:
    case DRIVER_LOGOUT:
    case DRIVER_SET_LOCATION:
    case DRIVER_SET_AVAILABILITY:
    case DRIVER_SET_AUTO_ACCEPT:
    case DRIVER_BOOKING_NEW:
    case DRIVER_BOOKING_STATE_CHANGE:
    case DRIVER_BOOKING_DELETE: {
      if (!state[action.payload.driverID]) {
        // Driver does not exist.
        return state;
      }
      return {
        ...state,
        [action.payload.driverID]: driver(state[action.payload.driverID], action, app),
      };
    }
    case DRIVER_DELETE:
      return _.omit(state, action.payload.driverID);
    case DRIVER_BATCH_SET_LOCATION:
      {
        const newState = {
          ...state,
        };
        _.each(action.payload.driverLocations, (location, driverID) => {
          newState[driverID] = {
            ...state[driverID],
            longitude: location[0],
            latitude: location[1],
          };
        });

        return newState;
      }
    default:
      return state;
  }
}

function cohort(state: Cohort = defaultCohortState, action: FSA, app: AppState): Cohort {
  switch (action.type) {
    case COHORT_CREATE + RequestResultCases.SUCCESS:
      return {
        ...state,
        cohortID: action.payload.cohortID,
        paxType: action.payload.paxType,
      };
    case COHORT_GET + RequestResultCases.SUCCESS:
      return {
        ...state,
        passengers: passengers(state.passengers, action),
        drivers: drivers(state.drivers, action, app),
        cohortID: action.meta && action.meta.cohortID,
        paxType: action.payload.paxType,
      };
    case COHORT_PASSENGER_ASSOCIATE:
    case COHORT_PASSENGER_ASSOCIATE + RequestResultCases.SUCCESS:
    case COHORT_PASSENGER_UPDATE:
    case PASSENGER_LOGIN:
    case PASSENGER_LOGIN + RequestResultCases.SUCCESS:
    case PASSENGER_LOGOUT:
    case PASSENGER_LOGOUT + RequestResultCases.SUCCESS:
    case PASSENGER_DELETE:
    case PASSENGER_DELETE + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_NEW:
    case PASSENGER_BOOKING_GET + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_STATE_CHANGE:
    case PASSENGER_BOOKING_CANCEL + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_DELETE:
    case PASSENGER_CHANGE_DESTINATION + RequestResultCases.SUCCESS:
      return {
        ...state,
        passengers: passengers(state.passengers, action),
      };
    case COHORT_DRIVER_ASSOCIATE:
    case COHORT_DRIVER_ASSOCIATE + RequestResultCases.SUCCESS:
    case COHORT_DRIVER_UPDATE:
    case DRIVER_LOGIN:
    case DRIVER_LOGIN + RequestResultCases.SUCCESS:
    case DRIVER_LOGOUT:
    case DRIVER_LOGOUT + RequestResultCases.SUCCESS:
    case DRIVER_DELETE:
    case DRIVER_DELETE + RequestResultCases.SUCCESS:
    case DRIVER_SET_AVAILABILITY:
    case DRIVER_SET_AVAILABILITY + RequestResultCases.SUCCESS:
    case DRIVER_SET_AUTO_ACCEPT:
    case DRIVER_SET_AUTO_ACCEPT + RequestResultCases.SUCCESS:
    case DRIVER_BOOKING_NEW:
    case DRIVER_BOOKING_STATE_CHANGE:
    case DRIVER_BOOKING_DELETE:
    case DRIVER_SET_LOCATION:
    case DRIVER_BATCH_SET_LOCATION:
      return {
        ...state,
        drivers: drivers(state.drivers, action, app),
      };
    default:
      return state;
  }
}

function cohorts(state: Cohorts = defaultCohortsState, action: FSA, app: AppState): Cohorts {
  switch (action.type) {
    case COHORT_CREATE + RequestResultCases.SUCCESS:
      return {
        ...state,
        [action.payload.cohortID]: cohort(state[action.payload.cohortID], action, app),
      };
    case COHORT_DELETE:
      return _.omit(state, action.payload.cohortID);
    // Directly remove non-existing cohorts to handle the case of fetching cohorts
    // that existed before a server restart.
    // TODO: When we persist cohorts data this can be removed.
    case COHORT_GET + RequestResultCases.FAILURE:
    case COHORT_DELETE + RequestResultCases.SUCCESS:
      if (!action.meta) {
        return state;
      }
      return _.omit(state, [action.meta.cohortID]);
    case UNSET_COHORT:
      return _.omit(state, [action.payload.cohortID]);
    case COHORT_GET + RequestResultCases.SUCCESS:
    case COHORT_PASSENGER_ASSOCIATE + RequestResultCases.SUCCESS:
    case PASSENGER_LOGIN + RequestResultCases.SUCCESS:
    case PASSENGER_LOGOUT + RequestResultCases.SUCCESS:
    case PASSENGER_DELETE + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_GET + RequestResultCases.SUCCESS:
    case PASSENGER_BOOKING_CANCEL + RequestResultCases.SUCCESS:
    case COHORT_DRIVER_ASSOCIATE + RequestResultCases.SUCCESS:
    case DRIVER_LOGIN + RequestResultCases.SUCCESS:
    case DRIVER_LOGOUT + RequestResultCases.SUCCESS:
    case DRIVER_DELETE + RequestResultCases.SUCCESS:
    case DRIVER_SET_AVAILABILITY + RequestResultCases.SUCCESS:
    case DRIVER_SET_AUTO_ACCEPT + RequestResultCases.SUCCESS:
    case PASSENGER_CHANGE_DESTINATION + RequestResultCases.SUCCESS:
      if (!action.meta) {
        return state;
      }
      return {
        ...state,
        [action.meta.cohortID]: cohort(state[action.meta.cohortID], action, app),
      };
    case COHORT_PASSENGER_ASSOCIATE:
    case COHORT_PASSENGER_UPDATE:
    case COHORT_DRIVER_ASSOCIATE:
    case COHORT_DRIVER_UPDATE:
    case DRIVER_LOGIN:
    case DRIVER_LOGOUT:
    case DRIVER_DELETE:
    case DRIVER_BOOKING_NEW:
    case DRIVER_BOOKING_STATE_CHANGE:
    case DRIVER_BOOKING_DELETE:
    case DRIVER_SET_LOCATION:
    case DRIVER_SET_AVAILABILITY:
    case DRIVER_SET_AUTO_ACCEPT:
    case PASSENGER_LOGIN:
    case PASSENGER_LOGOUT:
    case PASSENGER_DELETE:
    case PASSENGER_BOOKING_NEW:
    case PASSENGER_BOOKING_STATE_CHANGE:
    case PASSENGER_BOOKING_DELETE:
    case DRIVER_BATCH_SET_LOCATION:
      return {
        ...state,
        [action.payload.cohortID]: cohort(state[action.payload.cohortID], action, app),
      };
    default:
      return state;
  }
}

export default cohorts;



/** WEBPACK FOOTER **
 ** ./src/js/reducers/cohorts.js
 **/